const insights = [
  {
    tag: "UK Waste Data",
    title: "300,000 Tonnes in UK",
    body: "The UK alone generates over 300,000 tonnes of textile waste every year. The majority ends up in landfill or incineration, with less than 1% being recycled back into new clothing.",
    stat: "300K",
    statLabel: "tonnes of textile waste in UK yearly",
    img: "https://public.readdy.ai/ai/img_res/17c5802a47cf9ce19a630720f316b50a.jpg",
    accent: "#E63946",
  },
  {
    tag: "Consumer Psychology",
    title: "The Perfect Product Paradox",
    body: "Studies show consumers form deeper emotional bonds with imperfect objects. A chip, a stain, a visible repair — these trigger empathy, memory, and narrative attachment that flawless objects simply cannot replicate.",
    stat: "3×",
    statLabel: "longer kept than 'perfect' items",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/b985cd388307424a265809d0404e3f53.png",
    accent: "#1D3557",
  },
  {
    tag: "Craft & Materiality",
    title: "The Skilled Imperfection",
    body: "Traditional Japanese boro textiles and African adinkra patching demonstrate that intentional imperfection is a sophisticated cultural practice. The visible repair is not a failure — it is the mastery of knowing when to leave the mark.",
    stat: "100s",
    statLabel: "of years of wabi-sabi tradition",
    img: "https://readdy.ai/api/search-image?query=beautiful%20Japanese%20boro%20textile%20artwork%2C%20indigo%20patchwork%20fabric%20with%20visible%20stitching%20and%20repairs%2C%20traditional%20Japanese%20textile%20craft%2C%20rich%20textures%20of%20layered%20fabric%20repairs%2C%20museum%20quality%20photography%2C%20dark%20elegant%20background&width=600&height=400&seq=research003&orientation=landscape",
    accent: "#E63946",
  },
  {
    tag: "Design Theory",
    title: "Ugly-Cute as Aesthetic",
    body: "The \"ugly-cute\" aesthetic — kawaii, goblin mode, y2k — represents a generation reclaiming what mainstream culture rejects. Asymmetry, excess, the deliberately wrong — these have become the most powerful signals of authenticity and self-expression.",
    stat: "Gen Z",
    statLabel: "driving the ugly-cute movement",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/efd682b9a89d4a6c5cb3e56756f295aa.png",
    accent: "#1D3557",
  },
];

export default function ResearchSection() {
  return (
    <section id="research" className="bg-[#F5F3EF] py-24 md:py-36">
      <div className="w-full px-8 md:px-16">

        {/* Header */}
        <div className="flex items-center gap-4 mb-16">
          <span className="bg-[#0D0D0D] text-white font-mono text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full whitespace-nowrap">
            03 / 06
          </span>
          <div className="flex-1 h-[1px] bg-[#0D0D0D]/15" />
          <span className="font-mono text-[10px] text-[#0D0D0D]/30 uppercase tracking-[2px] whitespace-nowrap">
            Research &amp; Insight
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[3px] text-[#E63946] mb-4">
              The Data Behind Discarded
            </p>
            <h2
              className="font-display text-[#0D0D0D] leading-tight"
              style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
            >
              RESEARCH<br />
              <span className="text-[#1D3557]">&amp; INSIGHT</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-body text-[#2B2B2B]/70 text-lg leading-relaxed">
              Four critical perspectives that shaped OFF-PATTERN — from global waste data to design theory, cultural practice to consumer psychology.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {insights.map((insight, i) => (
            <div
              key={insight.title}
              className="bg-white rounded-3xl overflow-hidden group hover:shadow-none transition-all duration-300 border border-[#0D0D0D]/5"
            >
              {/* Image */}
              <div className="relative w-full overflow-hidden" style={{ height: "240px" }}>
                <img
                  src={insight.img}
                  alt={insight.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {/* Color Overlay */}
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: insight.accent, opacity: 0.15 }}
                />
                {/* Tag */}
                <div className="absolute top-4 left-4">
                  <span
                    className="font-mono text-[9px] uppercase tracking-[3px] px-2 py-1 rounded-full text-white"
                    style={{ backgroundColor: insight.accent }}
                  >
                    {insight.tag}
                  </span>
                </div>
                {/* Stat */}
                <div className="absolute bottom-4 right-4 text-right">
                  <span
                    className="font-display text-4xl text-white leading-none block"
                  >
                    {insight.stat}
                  </span>
                  <span className="font-mono text-[9px] text-white/70 uppercase tracking-[2px]">
                    {insight.statLabel}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="font-body text-[#0D0D0D] text-xl font-semibold mb-3 leading-tight">
                  {insight.title}
                </h3>
                <p className="font-body text-[#2B2B2B]/60 text-sm leading-relaxed">
                  {insight.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Quote */}
        <div className="mt-16 bg-[#1D3557] rounded-3xl p-10 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-1">
              <span className="font-display text-8xl text-white/20 leading-none">&ldquo;</span>
            </div>
            <div className="md:col-span-9">
              <p className="font-body text-white text-2xl md:text-3xl leading-relaxed font-light">
                The most interesting design questions are never about what meets the standard — they&apos;re about what refuses to.
              </p>
            </div>
            <div className="md:col-span-2 text-right">
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/40">
                OFF-PATTERN<br />Design Thesis
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
